import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { environment } from '../../../../environments/environment';
import { CompanionNumber, CompanionService } from '@services/companion.service';

/**
 * Campaign Companion (WhatsApp) connection card for the profile/settings page.
 * Connection is via EMAIL OTP done inside WhatsApp: the user messages the LuQta
 * bot, replies with their account email, and enters the emailed code. This card
 * shows the how-to, the user's connected numbers, and lets them enable/disable or
 * unlink a number.
 */
@Component({
  selector: 'app-companion-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './companion-card.component.html',
  styleUrls: ['./companion-card.component.scss'],
})
export class CompanionCardComponent implements OnInit {
  private companion = inject(CompanionService);
  private modal = inject(NzModalService);

  numbers: CompanionNumber[] = [];
  loading = false;

  /** The LuQta bot's WhatsApp number, shown as a wa.me link (digits only, no +). */
  botNumber = (environment as any).whatsappBotNumber || '';
  get botWaLink(): string {
    const n = (this.botNumber || '').replace(/[^0-9]/g, '');
    const text = encodeURIComponent('Hi LuQta, I want to connect my number.');
    return n ? `https://wa.me/${n}?text=${text}` : '';
  }

  ngOnInit(): void {
    this.loadNumbers();
  }

  loadNumbers(): void {
    this.loading = true;
    this.companion.listNumbers().subscribe({
      next: (rows) => {
        this.numbers = rows || [];
        this.loading = false;
      },
      error: () => {
        this.numbers = [];
        this.loading = false;
      },
    });
  }

  toggle(row: CompanionNumber): void {
    const next = !row.companion_enabled;
    this.companion.toggleNumber(row.id, next).subscribe({
      next: () => (row.companion_enabled = next),
      error: () => {},
    });
  }

  remove(row: CompanionNumber): void {
    this.modal.confirm({
      nzTitle: 'Disconnect this number?',
      nzContent: `${row.phone_number} will no longer be able to use the WhatsApp assistant.`,
      nzOkText: 'Disconnect',
      nzOkDanger: true,
      nzOnOk: () => {
        this.companion.removeNumber(row.id).subscribe({
          next: () => (this.numbers = this.numbers.filter((n) => n.id !== row.id)),
          error: () => {},
        });
      },
    });
  }

  statusLabel(row: CompanionNumber): string {
    if (!row.is_verified) return 'Not verified';
    return row.companion_enabled ? 'Connected' : 'Disabled';
  }

  statusClass(row: CompanionNumber): string {
    if (!row.is_verified) return 'status-pending';
    return row.companion_enabled ? 'status-connected' : 'status-disabled';
  }
}
